import {
  DownOutlined,
  InfoCircleOutlined,
  MailOutlined,
  MobileOutlined,
  UpOutlined
} from "@ant-design/icons"
import { Alert, App, Button, Drawer, Form, Grid, Skeleton } from "antd"
import React, { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"

import { TextArea } from "../../../components/Input/TextArea.tsx"
import { useDateFormatter } from "../../../hooks/use-date-formatter.tsx"
import { unwrapEnvelope } from "../../../utils/api/unwrap-envelope.ts"
import { requestMarketingSms } from "../api/consent.ts"
import { useCommunicationRights } from "../communication-permissions.ts"
import {
  type ChangeForm,
  ConsentChangeFields,
  initialChange,
  useConsentSave
} from "../consent/ConsentChangeModal.tsx"
import { StatusDot } from "../hub/ui.tsx"
import { ConsentEventEntry } from "../consent/ConsentEventEntry.tsx"
import { formatUsPhone } from "../settings/format-phone.ts"
import type { Channel } from "../types/compose.types.ts"
import type {
  ConsentEvent,
  ConsentState,
  ContactConsent
} from "../types/consent.types.ts"
import { ChannelChips } from "./ContactStrip.tsx"
import { storeConsent, useContactConsent } from "./consent-store.ts"
import { summarizeChannel } from "./consent-summary.ts"
import type { CommContact } from "./record-contacts.ts"

const CHANNELS: Channel[] = ["email", "sms"]

const CHANNEL_ICON: Record<Channel, React.ReactNode> = {
  email: <MailOutlined aria-hidden />,
  sms: <MobileOutlined aria-hidden />
}

/** Where a change to this contact is kept, said only as far as the backend keeps it. */
const scopeKey = (contact: CommContact) =>
  contact.role === "customer"
    ? "recordComm.prefs.scope.customer"
    : contact.role === "lead"
      ? "recordComm.prefs.scope.lead"
      : "recordComm.prefs.scope.business"

const ChangeFormPanel = ({
  contact,
  channel,
  standing,
  onCancel,
  onSaved,
  onDirty
}: {
  contact: CommContact
  channel: Channel
  standing: ConsentState
  onCancel: () => void
  onSaved: () => void
  onDirty: () => void
}) => {
  const { t } = useTranslation()
  const [form] = Form.useForm<ChangeForm>()
  const { save, saving, failed } = useConsentSave({
    contact: contact.ref,
    channel,
    form,
    onSaved: (next) => {
      storeConsent(contact.ref, next)
      onSaved()
    }
  })
  const stateLabel = (state: ConsentState) =>
    t(`recordComm.state.${channel}.${state}`)

  return (
    <div
      className="mt-3 flex flex-col gap-3 rounded-lg bg-slate-50 p-4"
      aria-busy={saving}
    >
      <p className="m-0 flex items-start gap-2 rounded-md bg-[var(--dk-selected,#EEF7F6)] p-3 text-sm text-slate-700">
        <InfoCircleOutlined
          aria-hidden
          className="mt-0.5 text-[var(--dk-primary-text,#2A7D8C)]"
        />
        {t(`recordComm.prefs.notAddress.${channel}`)}
      </p>
      <div className="flex items-center gap-3 text-sm">
        <span className="text-slate-600">
          {t("recordComm.prefs.currentStatus")}
        </span>
        <StatusDot
          pill
          tone={standing === "allowed" ? "success" : "danger"}
          label={stateLabel(standing)}
        />
      </div>
      <Form<ChangeForm>
        form={form}
        layout="vertical"
        requiredMark
        onFinish={save}
        onValuesChange={() => onDirty()}
        initialValues={initialChange(channel, standing)}
        className="[&_.ant-form-item]:mb-3"
      >
        <ConsentChangeFields
          channel={channel}
          autoFocus
          stateLabel={t("recordComm.prefs.newStatus")}
          dateLabel={t("recordComm.prefs.dateRequested")}
          optionLabel={stateLabel}
          reasonInput={
            <TextArea
              rows={3}
              maxLength={255}
              showCount
              placeholder={t("communication.consent.reasonPlaceholder")}
            />
          }
        />
        <p className="m-0 mb-3 text-xs text-slate-500">
          {t("recordComm.prefs.recordsWho")}
        </p>
        {failed && (
          <Alert
            type="error"
            showIcon
            className="mb-3"
            message={t("communication.consent.saveFailed")}
          />
        )}
        <div className="flex justify-end gap-2">
          <Button onClick={onCancel} disabled={saving}>
            {t("recordComm.prefs.cancel")}
          </Button>
          <Button type="primary" htmlType="submit" loading={saving}>
            {saving ? t("recordComm.prefs.saving") : t("recordComm.prefs.save")}
          </Button>
        </div>
      </Form>
    </div>
  )
}

const MarketingRequest = ({
  contact,
  consent,
  onSent
}: {
  contact: CommContact
  consent: ContactConsent
  onSent: () => void
}) => {
  const { t } = useTranslation()
  const { message } = App.useApp()
  const rights = useCommunicationRights()
  const [requesting, setRequesting] = useState(false)
  const sms = consent.channels.sms
  const mayAsk =
    rights.send &&
    (contact.role === "customer" || contact.role === "lead") &&
    !!sms.address &&
    sms.available &&
    sms.state === "allowed" &&
    !sms.marketing_opt_in &&
    !sms.marketing_requested_at
  if (!mayAsk) return null

  const ask = async () => {
    setRequesting(true)
    const response = await requestMarketingSms(contact.ref)
    setRequesting(false)
    if (!response.ok) {
      const detail = (
        response.data as { details?: { detail?: unknown } } | undefined
      )?.details?.detail
      message.error(
        typeof detail === "string"
          ? detail
          : t("communication.consent.requestFailed")
      )
      return
    }
    const sent = unwrapEnvelope<{ status: string; reason: string }>(
      response.data
    )
    if (sent.status === "skipped") {
      message.warning(
        t(`communication.composer.reasons.${sent.reason}`, {
          defaultValue: t("communication.consent.requestFailed")
        })
      )
      return
    }
    message.success(t("communication.consent.requestSent"))
    onSent()
  }

  return (
    <Button size="small" loading={requesting} onClick={() => void ask()}>
      {t("communication.consent.requestMarketing")}
    </Button>
  )
}

const ChannelSection = ({
  contact,
  consent,
  channel,
  open,
  onToggle,
  onSaved,
  onDirty,
  onReload
}: {
  contact: CommContact
  consent: ContactConsent
  channel: Channel
  open: boolean
  onToggle: () => void
  onSaved: () => void
  onDirty: () => void
  onReload: () => void
}) => {
  const { t } = useTranslation()
  const { formatDate } = useDateFormatter()
  const rights = useCommunicationRights()
  const state = consent.channels[channel]
  const summary = summarizeChannel(consent, channel)
  const headingId = `prefs-${channel}-heading`
  const changeLabel = t(`recordComm.prefs.change.${channel}`)

  return (
    <section
      aria-labelledby={headingId}
      className="rounded-lg border border-solid border-slate-200 bg-surface p-4"
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex min-w-0 items-start gap-3">
          <span className="mt-0.5 text-xl text-slate-600">
            {CHANNEL_ICON[channel]}
          </span>
          <div className="flex min-w-0 flex-col gap-1">
            <h3
              id={headingId}
              className="m-0 text-base font-semibold text-slate-900"
            >
              {t(`commHub.channel.${channel}`)}
            </h3>
            <span className="truncate text-sm text-[var(--dk-primary-text,#2A7D8C)]">
              {state.address
                ? channel === "sms"
                  ? formatUsPhone(state.address)
                  : state.address
                : t(
                    `recordComm.status.${channel === "sms" ? "noPhone" : "noEmail"}`
                  )}
            </span>
            <ChannelChips summary={summary} />
            <span className="text-xs text-slate-500">
              {t("recordComm.prefs.preference", {
                state: t(`recordComm.state.${channel}.${state.state}`)
              })}
              {state.state_changed_at
                ? ` · ${t("communication.consent.since", {
                    date: formatDate(state.state_changed_at),
                    source: t(
                      `communication.consent.source.${state.state_source}`,
                      { defaultValue: state.state_source }
                    )
                  })}`
                : ""}
            </span>
            {state.suppressed && (
              <span className="text-xs text-amber-700">
                {t("communication.consent.blockedSince", {
                  reason: t(
                    `communication.consent.blockedReason.${state.suppressed.reason}`,
                    { defaultValue: state.suppressed.reason }
                  ),
                  date: formatDate(state.suppressed.since)
                })}
              </span>
            )}
            {channel === "sms" && state.marketing_requested_at && (
              <span className="text-xs text-slate-500">
                {t("communication.consent.marketingRequested", {
                  date: formatDate(state.marketing_requested_at)
                })}
              </span>
            )}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {channel === "sms" && (
            <MarketingRequest
              contact={contact}
              consent={consent}
              onSent={onReload}
            />
          )}
          {rights.send && (
            <Button
              onClick={onToggle}
              aria-expanded={open}
              aria-controls={`prefs-${channel}-form`}
              icon={
                open ? <UpOutlined aria-hidden /> : <DownOutlined aria-hidden />
              }
              iconPosition="end"
            >
              {changeLabel}
            </Button>
          )}
        </div>
      </div>
      {open && (
        <div id={`prefs-${channel}-form`}>
          <ChangeFormPanel
            contact={contact}
            channel={channel}
            standing={state.state}
            onCancel={onToggle}
            onSaved={onSaved}
            onDirty={onDirty}
          />
        </div>
      )}
    </section>
  )
}

const HistoryList = ({ events }: { events: ConsentEvent[] }) => {
  const { t } = useTranslation()
  return (
    <section aria-labelledby="prefs-history" className="flex flex-col gap-3">
      <h3
        id="prefs-history"
        className="m-0 text-sm font-semibold text-slate-700"
      >
        {t("recordComm.prefs.history")}
      </h3>
      {events.length === 0 ? (
        <p className="m-0 text-sm text-slate-500">
          {t("recordComm.prefs.noHistory")}
        </p>
      ) : (
        <ol className="m-0 flex list-none flex-col gap-3 p-0">
          {events.map((event) => (
            <ConsentEventEntry key={event.id} event={event} />
          ))}
        </ol>
      )}
    </section>
  )
}

/**
 * A contact's email and text preferences, changed with a reason and the day
 * the person asked, and the history of every change. Changes the preference,
 * never the address.
 */
export const ContactPreferencesPanel = ({
  contact,
  open,
  onClose
}: {
  contact: CommContact
  open: boolean
  onClose: () => void
}) => {
  const { t } = useTranslation()
  const { modal } = App.useApp()
  const screens = Grid.useBreakpoint()
  const { load, reload } = useContactConsent(open ? contact.ref : null)
  const [editing, setEditing] = useState<Channel | null>(null)
  const [dirty, setDirty] = useState(false)

  useEffect(() => {
    if (!open) {
      setEditing(null)
      setDirty(false)
    }
  }, [open])

  const guard = (proceed: () => void) => {
    if (!dirty) {
      proceed()
      return
    }
    modal.confirm({
      title: t("recordComm.prefs.discardTitle"),
      content: t("recordComm.prefs.discardBody"),
      okText: t("recordComm.prefs.discard"),
      cancelText: t("recordComm.prefs.keepEditing"),
      okButtonProps: { danger: true },
      autoFocusButton: "cancel",
      onOk: () => {
        setDirty(false)
        proceed()
      }
    })
  }

  const toggle = (channel: Channel) =>
    guard(() => {
      setDirty(false)
      setEditing((current) => (current === channel ? null : channel))
    })

  const name = contact.name || t("recordComm.contact.unnamed")

  return (
    <Drawer
      open={open}
      onClose={() => guard(onClose)}
      width={screens.md ? 460 : "100%"}
      destroyOnHidden
      title={
        <div className="flex flex-col">
          <span className="text-lg font-semibold text-slate-900">
            {t("recordComm.prefs.title")}
          </span>
          <span className="text-sm font-normal text-slate-500">{name}</span>
        </div>
      }
    >
      <div className="flex flex-col gap-4 [&_:is(h1,h2,h3)]:text-left">
        <p className="m-0 text-sm text-slate-600">
          {t(scopeKey(contact), {
            role: t(`recordComm.contact.role.${contact.role}`)
          })}
        </p>
        {load.state === "loading" && (
          <Skeleton active paragraph={{ rows: 6 }} />
        )}
        {load.state === "forbidden" && (
          <Alert
            type="info"
            showIcon
            message={t("recordComm.contact.noAccess")}
          />
        )}
        {load.state === "failed" && (
          <Alert
            type="warning"
            showIcon
            message={t("communication.consent.loadFailed")}
            action={
              <Button size="small" onClick={reload}>
                {t("commHub.common.retry")}
              </Button>
            }
          />
        )}
        {load.state === "ready" && (
          <>
            {CHANNELS.map((channel) => (
              <ChannelSection
                key={channel}
                contact={contact}
                consent={load.consent}
                channel={channel}
                open={editing === channel}
                onToggle={() => toggle(channel)}
                onSaved={() => {
                  setDirty(false)
                  setEditing(null)
                }}
                onDirty={() => setDirty(true)}
                onReload={reload}
              />
            ))}
            <HistoryList events={load.consent.history} />
          </>
        )}
      </div>
    </Drawer>
  )
}
